import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Compass, Lightbulb, MessageCircle, Map as MapIcon, SlidersHorizontal,
  ShieldCheck, Lock, Loader2, Upload, RefreshCw, Eye, Pencil, Briefcase,
  ChevronLeft, ChevronRight, Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthModal } from '../hooks/useAuthModal';
import AuthModal from '../components/Auth/AuthModal';
import {
  getCvPassport, getCvPublicProfile, type CvPassport, type CvPublicProfile,
} from '../api/passport';
import StationsView from '../components/Passport/StationsView';
import SkillsView from '../components/Passport/SkillsView';
import RealTalkView from '../components/Passport/RealTalkView';
import MapView from '../components/Passport/MapView';
import PassportViewer from '../components/Passport/PassportViewer';
import CvPassportPrefs from '../components/Passport/CvPassportPrefs';
import CvPassportRealTalk from '../components/Passport/CvPassportRealTalk';
import AtsReadabilityCard from '../components/Passport/AtsReadabilityCard';
import ExplorerPreScreen from '../components/Passport/ExplorerPreScreen';

type ViewAs = 'edit' | 'recruiter' | 'explorer';
type PageKey = 'cv' | 'stations' | 'skills' | 'realtalk' | 'map' | 'prefs' | 'prescreen';

const PUBLIC_PAGES: PageKey[] = ['cv', 'stations', 'map', 'skills', 'realtalk'];
const RECRUITER_PAGES: PageKey[] = ['prefs', 'prescreen'];

const PAGE_ICONS: Record<PageKey, React.ComponentType<{ className?: string }>> = {
  cv: FileText,
  stations: Compass,
  skills: Lightbulb,
  realtalk: MessageCircle,
  map: MapIcon,
  prefs: SlidersHorizontal,
  prescreen: ShieldCheck,
};

const isUploadInProgress = (): boolean => {
  const ts = localStorage.getItem('cv_upload_timestamp');
  if (!ts) return false;
  try {
    return Date.now() - parseInt(ts, 10) < 6 * 60 * 1000;
  } catch {
    return false;
  }
};

const MyPassport: React.FC = () => {
  const { t } = useTranslation(['passport', 'profile']);
  const navigate = useNavigate();
  const { user, apiCall } = useAuth();
  const { showAuthModal, setShowAuthModal } = useAuthModal();
  // CV upload lives on the dedicated full-page flow (Inspire tab) — the canvas
  // anonymizer needs full width, so we route there instead of a cramped modal.
  const goToUpload = () => navigate('/?tab=inspire');

  const [cvId, setCvId] = useState<string | null>(null);

  const [passport, setPassport] = useState<CvPassport | null>(null);
  const [realTalk, setRealTalk] = useState<CvPublicProfile | null>(null);
  const [images, setImages] = useState<string[] | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [searchParams] = useSearchParams();
  const [viewAs, setViewAs] = useState<ViewAs>('edit');
  // Deep-link support: ?pp=prefs | ?pp=realtalk (also used by nudges / upload success).
  const ppParam = searchParams.get('pp') as PageKey | null;
  const [page, setPage] = useState<PageKey>(ppParam || 'cv');
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Load own profile → cv_id.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.userId) return;
      try {
        const res = await apiCall(`${apiBase}/api/v1/profiles/${user.userId}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        setCvId(data.cv_id || null);
      } catch {
        /* ignore */
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  // Load derived content for the current cv_id (passport JSON, real talk, images).
  const loadContent = useCallback(async (attempt = 0) => {
    if (!cvId) return;
    if (attempt === 0) setContentLoading(true);
    try {
      const [p, rt, imgRes] = await Promise.all([
        getCvPassport(apiCall, cvId).catch(() => null),
        getCvPublicProfile(apiCall, cvId).catch(() => null),
        apiCall(`${apiBase}/api/v1/cv/${cvId}/images`).then(r => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      setPassport(p);
      setRealTalk(rt);
      setImages(imgRes?.images ?? []);

      // Still processing right after upload → retry a few times so the page
      // fills in without a manual refresh (worker pipeline is async).
      const notReady = !p || !(imgRes?.images?.length);
      if (notReady && attempt < 4) {
        retryTimer.current = setTimeout(() => loadContent(attempt + 1), 4000 * (attempt + 1));
      }
    } finally {
      setContentLoading(false);
    }
  }, [cvId, apiCall, apiBase]);

  useEffect(() => {
    if (cvId) loadContent(0);
    return () => { if (retryTimer.current) clearTimeout(retryTimer.current); };
  }, [cvId, loadContent]);

  const handleDeleteCv = useCallback(async () => {
    if (!cvId || deleting) return;
    if (!window.confirm(t('profile:cv.deleteWarning'))) return;

    setDeleting(true);
    try {
      const response = await apiCall(`${apiBase}/api/v1/cvs/${cvId}`, { method: 'DELETE' });
      if (!response.ok) {
        setDeleting(false);
        window.alert(t('profile:errors.deleteCV'));
        return;
      }
      if (retryTimer.current) clearTimeout(retryTimer.current);
      localStorage.removeItem('cv_upload_timestamp');
      setCvId(null);
      setPassport(null);
      setRealTalk(null);
      setImages(null);
      setPage('cv');
    } catch {
      window.alert(t('profile:errors.deleteCV'));
    } finally {
      setDeleting(false);
    }
  }, [cvId, deleting, apiCall, apiBase, t]);

  const hasCv = !!cvId;
  const processing = hasCv && (!passport || !(images && images.length > 0)) && (isUploadInProgress() || contentLoading);

  const pages: PageKey[] = useMemo(() => {
    // Real Talk is explorer-only — recruiters never see it.
    if (viewAs === 'recruiter') {
      return [...PUBLIC_PAGES.filter((p) => p !== 'realtalk'), ...RECRUITER_PAGES];
    }
    // edit: everything; explorer: handled separately via PassportViewer.
    return [...PUBLIC_PAGES, ...RECRUITER_PAGES];
  }, [viewAs]);

  // Keep selected tab valid when the page set changes (e.g. switching to explorer).
  useEffect(() => {
    if (!pages.includes(page)) setPage('cv');
  }, [pages, page]);

  // Honor ?pp= when navigated externally while Passport is already mounted.
  useEffect(() => {
    const pp = searchParams.get('pp') as PageKey | null;
    if (pp && pages.includes(pp) && pp !== page) setPage(pp);
  }, [searchParams, pages, page]);

  const pageIdx = Math.max(0, pages.indexOf(page));
  const goTo = (idx: number) => setPage(pages[Math.max(0, Math.min(pages.length - 1, idx))]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-40">
      <div className="max-w-3xl mx-auto px-3 sm:px-6 pt-4 sm:pt-6">
        {/* Header: title + View-as switch */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h1 className="text-lg sm:text-2xl font-bold text-slate-800">
            {t('passport:headingPrefix')}{t('passport:headingHighlight')}
          </h1>
          <ViewAsSwitch value={viewAs} onChange={setViewAs} t={t} />
        </div>

        {/* ─── Explorer preview: exactly what explorers see (anonymized). ─── */}
        {viewAs === 'explorer' ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 border-b border-slate-100">
              <Eye className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] sm:text-xs text-slate-500 leading-relaxed">{t('passport:explorerPreview.note')}</p>
            </div>
            {hasCv ? (
              <div className="h-[70vh]">
                <PassportViewer cvId={cvId!} />
              </div>
            ) : (
              <>
                <StaticPublicTabs />
                <NoCvState t={t} onUpload={goToUpload} />
              </>
            )}
          </div>
        ) : (
          /* ─── Edit / Recruiter: full pager incl. recruiter-only pages. ─── */
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            {/* Tabs (two rows on mobile via wrap, single even row on desktop) */}
            <div className="border-b border-slate-100">
              <div className="max-w-2xl mx-auto px-2 sm:px-3 flex flex-wrap sm:flex-nowrap justify-center">
                {pages.map((key, i) => {
                  const Icon = PAGE_ICONS[key];
                  const active = i === pageIdx;
                  const isRecruiterPage = RECRUITER_PAGES.includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => setPage(key)}
                      className={`relative grow ${viewAs === 'recruiter' ? 'basis-1/4' : 'basis-1/5'} sm:basis-0 sm:flex-1 flex items-center justify-center gap-0.5 sm:gap-1.5 px-1 py-2.5 text-[10px] sm:text-[13px] font-semibold whitespace-nowrap transition-colors ${
                        active ? 'text-pink-600' : 'text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      {isRecruiterPage
                        ? <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                        : <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />}
                      <span>{t(`passport:tabs.${key}`)}</span>
                      {active && (
                        <motion.div
                          layoutId="myPassportTab"
                          className="absolute bottom-0 left-2 right-2 h-[2.5px] rounded-full bg-gradient-to-r from-pink-400 to-rose-500"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="relative min-h-[60vh]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={page + viewAs}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                >
                  <PageBody
                    page={page}
                    viewAs={viewAs}
                    cvId={cvId}
                    hasCv={hasCv}
                    processing={processing}
                    images={images}
                    passport={passport}
                    realTalk={realTalk}
                    onUpload={goToUpload}
                    onDelete={handleDeleteCv}
                    deleting={deleting}
                    t={t}
                  />
                </motion.div>
              </AnimatePresence>

              {/* Desktop page arrows for public pages */}
              {pageIdx > 0 && (
                <button
                  onClick={() => goTo(pageIdx - 1)}
                  className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white/90 border border-slate-200 shadow-sm text-slate-500 hover:text-pink-500"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              {pageIdx < pages.length - 1 && (
                <button
                  onClick={() => goTo(pageIdx + 1)}
                  className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white/90 border border-slate-200 shadow-sm text-slate-500 hover:text-pink-500"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {deleting && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[10001]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <div className="fixed inset-0 flex items-center justify-center z-[10001] px-4">
              <motion.div
                className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              >
                <div className="bg-gradient-to-r from-red-500 to-rose-500 p-5 text-center">
                  <Trash2 className="w-10 h-10 text-white mx-auto mb-2 drop-shadow" />
                  <h3 className="text-base sm:text-lg font-semibold text-white">
                    {t('profile:deleteCV.title')}
                  </h3>
                </div>
                <div className="p-5 text-center">
                  <div className="flex justify-center mb-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-red-200 border-t-red-500" />
                  </div>
                  <p className="text-sm text-slate-600">{t('profile:deleteCV.message')}</p>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} signupPath="/signup" />
    </div>
  );
};

/* ─────────────────────────── sub-components ─────────────────────────── */

const ViewAsSwitch: React.FC<{ value: ViewAs; onChange: (v: ViewAs) => void; t: any }> = ({ value, onChange, t }) => {
  const opts: { id: ViewAs; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'edit', label: t('passport:viewAs.edit'), Icon: Pencil },
    { id: 'recruiter', label: t('passport:viewAs.recruiter'), Icon: Briefcase },
    { id: 'explorer', label: t('passport:viewAs.explorer'), Icon: Eye },
  ];
  return (
    <div className="inline-flex items-center gap-0.5 p-0.5 bg-slate-100 rounded-full self-start">
      {opts.map(({ id, label, Icon }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={`flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold transition-all ${
            value === id ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Icon className="w-3.5 h-3.5" />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
};

/* Visual-only tab strip shown above the explorer empty state so the bar stays
   consistent with the edit/recruiter views (and the real PassportViewer). */
const StaticPublicTabs: React.FC = () => {
  const { t } = useTranslation(['passport']);
  return (
    <div className="border-b border-slate-100">
      <div className="max-w-2xl mx-auto px-3 flex">
        {PUBLIC_PAGES.map((key, i) => {
          const Icon = PAGE_ICONS[key];
          const active = i === 0;
          return (
            <div
              key={key}
              className={`relative flex-1 min-w-0 flex items-center justify-center gap-0.5 sm:gap-1.5 px-0.5 sm:px-1 py-2.5 text-[10px] sm:text-[13px] font-semibold ${
                active ? 'text-pink-600' : 'text-slate-300'
              }`}
            >
              <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
              <span className="truncate">{t(`passport:tabs.${key}`)}</span>
              {active && (
                <div className="absolute bottom-0 left-2 right-2 h-[2.5px] rounded-full bg-gradient-to-r from-pink-400 to-rose-500" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const NoCvState: React.FC<{ t: any; onUpload: () => void }> = ({ t, onUpload }) => (
  <div className="flex items-center justify-center py-16 px-4">
    <div className="text-center max-w-sm">
      <div className="mx-auto w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mb-3">
        <Upload className="w-6 h-6 text-pink-500" />
      </div>
      <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-1">{t('passport:states.noCvTitle')}</h3>
      <p className="text-xs sm:text-sm text-slate-600 mb-4 leading-relaxed">{t('passport:states.noCvSubtitle')}</p>
      <button
        onClick={onUpload}
        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-full shadow-sm hover:from-pink-600 hover:to-rose-600 transition-all"
      >
        <Upload className="w-4 h-4" />
        {t('passport:states.noCvCta')}
      </button>
    </div>
  </div>
);

const GeneratingState: React.FC<{ t: any }> = ({ t }) => (
  <div className="flex items-center justify-center py-16 px-4">
    <div className="text-center max-w-sm">
      <Loader2 className="w-7 h-7 text-pink-500 animate-spin mx-auto mb-3" />
      <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-1">{t('passport:states.generatingTitle')}</h3>
      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{t('passport:states.generatingSubtitle')}</p>
    </div>
  </div>
);

interface PageBodyProps {
  page: PageKey;
  viewAs: ViewAs;
  cvId: string | null;
  hasCv: boolean;
  processing: boolean;
  images: string[] | null;
  passport: CvPassport | null;
  realTalk: CvPublicProfile | null;
  onUpload: () => void;
  onDelete: () => void;
  deleting: boolean;
  t: any;
}

const PageBody: React.FC<PageBodyProps> = ({
  page, viewAs, cvId, hasCv, processing, images, passport, realTalk, onUpload, onDelete, deleting, t,
}) => {
  const editable = viewAs === 'edit';

  // Recruiter-only pages — never gated by CV presence.
  if (page === 'prefs') return <CvPassportPrefs readOnly={!editable} />;
  if (page === 'prescreen') return <ExplorerPreScreen />;

  // Editable Real Talk / contact only needs the profile, not a CV.
  if (page === 'realtalk' && editable) return <CvPassportRealTalk />;

  // Public pages: handle empty / processing first.
  if (!hasCv) {
    if (page === 'cv') return <NoCvState t={t} onUpload={onUpload} />;
    return (
      <div className="flex items-center justify-center py-16 px-4">
        <div className="text-center max-w-sm">
          <div className="mx-auto w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mb-3">
            <Upload className="w-6 h-6 text-pink-500" />
          </div>
          <p className="text-xs sm:text-sm text-slate-600 mb-4 leading-relaxed">{t('passport:states.noCvSubtitle')}</p>
          <button
            onClick={onUpload}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-full shadow-sm"
          >
            <Upload className="w-4 h-4" /> {t('passport:states.noCvCta')}
          </button>
        </div>
      </div>
    );
  }

  if (page === 'cv') {
    return (
      <div className="max-w-2xl mx-auto px-3 py-4">
        {editable && (
          <div className="flex justify-end gap-2 mb-3">
            <button
              onClick={onUpload}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-pink-600 border border-pink-200 rounded-lg hover:bg-pink-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="w-3.5 h-3.5" /> {t('passport:states.replaceCv')}
            </button>
            <button
              onClick={onDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('profile:cv.delete')}
            >
              <Trash2 className="w-3.5 h-3.5" /> {t('profile:cv.delete')}
            </button>
          </div>
        )}
        {/* Owner-only ATS readability (private — never shown to recruiters/explorers) */}
        {editable && cvId && images && images.length > 0 && <AtsReadabilityCard cvId={cvId} />}
        {images === null ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> …
          </div>
        ) : images.length === 0 ? (
          <GeneratingState t={t} />
        ) : (
          <div className="space-y-3">
            {images.map((img, i) => (
              <img
                key={i}
                src={`data:image/png;base64,${img}`}
                alt={`CV page ${i + 1}`}
                className="w-full rounded-lg border border-slate-200 shadow-sm"
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Derived pages need the passport JSON.
  if (!passport) {
    return processing ? <GeneratingState t={t} /> : <GeneratingState t={t} />;
  }

  if (page === 'stations') return <StationsView stations={passport.stations ?? []} />;
  if (page === 'skills') {
    return (
      <SkillsView
        skills={passport.skills ?? []}
        languages={passport.languages ?? []}
        interests={passport.interests ?? []}
        highlights={passport.highlights ?? []}
      />
    );
  }
  if (page === 'map') return <MapView locations={passport.locations ?? []} stations={passport.stations ?? []} />;
  if (page === 'realtalk') {
    // Edit mode is handled earlier; here it's the recruiter/explorer read-only view.
    return <RealTalkView sentence={realTalk?.real_talk_sentence ?? null} tags={realTalk?.real_talk_tags ?? null} />;
  }
  return null;
};

export default MyPassport;
